/**
 * Home / Portal Demo — tasks + task-comments store.
 *
 * THROWAWAY DEMO CODE. The Tasks tab's single human-in-the-loop task and its
 * comment thread live here in a resettable store, so the chat actions (comment,
 * comment & return to agent, status changes) stick during a session and the
 * Cmd+K "Reset demo" restores them. Served onto the real Inbox via the home
 * resolver (`tasks.list`, `comments.list` / `comments.create`,
 * `tasks.assign` / `tasks.batchUpdate` / `tasks.batchReturnToAgent`).
 *
 * Delete with src/demo/home.
 */

import type { CommentResponse, TaskResponse } from '@erp/rpc';
import { createDemoStore } from '@/demo/core/create-demo-store';

type TaskStatus = TaskResponse['status'];

const ORG_ID = 'c1000000-0000-4000-8000-000000000000';
const TASK_ID = 'c1000000-0000-4000-8000-000000000004';

/** The sourcing agent the task is escalated from / returned to. */
export const HOME_DEMO_SOURCING_AGENT_ID =
  'c1000000-0000-4000-8000-0000000000b2';

interface HomeTasksState {
  tasks: TaskResponse[];
  /** Comments keyed by task id. */
  comments: Record<string, CommentResponse[]>;
}

function minutesAgo(min: number): Date {
  return new Date(Date.now() - min * 60_000);
}

function buildSeed(): HomeTasksState {
  const task: TaskResponse = {
    id: TASK_ID,
    organizationId: ORG_ID,
    title: 'Sourcing agent needs your input',
    description:
      'An inbound supplier quote includes a freight surcharge the agent has no GL-coding rule for. It paused and is asking how to code it.',
    status: 'in_progress',
    priority: 'medium',
    assigneeType: 'agent',
    assigneeUserId: null,
    assigneeAgentDefinitionId: HOME_DEMO_SOURCING_AGENT_ID,
    createdByUserId: null,
    dueDate: null,
    startedAt: null,
    completedAt: null,
    metadata: {
      blockedReason: 'Which GL account should freight surcharges post to?',
    },
    tags: null,
    createdAt: minutesAgo(240),
    updatedAt: minutesAgo(26),
    isWatching: true,
  };

  const comments: CommentResponse[] = [
    {
      id: 'c1000000-0000-4000-8000-0000000000c1',
      organizationId: ORG_ID,
      commentableType: 'TASK',
      commentableId: TASK_ID,
      authorType: 'agent',
      authorUserId: null,
      authorAgentDefinitionId: HOME_DEMO_SOURCING_AGENT_ID,
      body: "I'm quoting RFQ-4471 and the supplier added a $240 freight surcharge. I don't have a GL-coding rule for freight on this customer — which account should it post to? Reply and I'll apply it and remember the rule for next time.",
      attachments: null,
      readAt: null,
      readByAgentDefinitionId: null,
      createdAt: minutesAgo(26),
      updatedAt: minutesAgo(26),
    },
  ];

  return { tasks: [task], comments: { [TASK_ID]: comments } };
}

export const homeTasksStore = createDemoStore(buildSeed);

function paginate<T>(items: T[], limit: number) {
  return {
    total: items.length,
    limit,
    offset: 0,
    hasMore: false,
  };
}

/** `tasks.list` — the single demo task, optionally filtered by the search box. */
export function listHomeTasks(search?: string) {
  const all = homeTasksStore.getState().tasks;
  const q = search?.trim().toLowerCase();
  const tasks = q
    ? all.filter((t) =>
        [t.title, t.description ?? '', JSON.stringify(t.metadata ?? {})]
          .join(' ')
          .toLowerCase()
          .includes(q),
      )
    : all;
  return { tasks, pagination: paginate(tasks, 100) };
}

/** `comments.list` for a task. */
export function listHomeTaskComments(taskId: string) {
  const comments = homeTasksStore.getState().comments[taskId] ?? [];
  return { comments, pagination: paginate(comments, 200) };
}

/** `comments.create` — append a user comment and return it. */
export function createHomeTaskComment(
  taskId: string,
  body: string,
): CommentResponse {
  const now = new Date();
  const comment: CommentResponse = {
    id: crypto.randomUUID(),
    organizationId: ORG_ID,
    commentableType: 'TASK',
    commentableId: taskId,
    authorType: 'user',
    authorUserId: null,
    authorAgentDefinitionId: null,
    body,
    attachments: null,
    readAt: null,
    readByAgentDefinitionId: null,
    createdAt: now,
    updatedAt: now,
  };
  homeTasksStore.update((s) => {
    s.comments[taskId] = [...(s.comments[taskId] ?? []), comment];
  });
  return comment;
}

/** `tasks.assign` — (re)assign a task to an agent. */
export function assignHomeTaskToAgent(
  taskId: string,
  agentId: string,
): TaskResponse | undefined {
  let updated: TaskResponse | undefined;
  homeTasksStore.update((s) => {
    const t = s.tasks.find((x) => x.id === taskId);
    if (!t) return;
    t.assigneeType = 'agent';
    t.assigneeAgentDefinitionId = agentId || t.assigneeAgentDefinitionId;
    t.assigneeUserId = null;
    t.updatedAt = new Date();
    updated = t;
  });
  return updated;
}

/** `tasks.batchUpdate` — set status on the given tasks. */
export function updateHomeTaskStatus(
  taskIds: string[],
  status: TaskStatus,
): TaskResponse[] {
  const updated: TaskResponse[] = [];
  homeTasksStore.update((s) => {
    for (const t of s.tasks) {
      if (!taskIds.includes(t.id)) continue;
      t.status = status;
      t.updatedAt = new Date();
      updated.push(t);
    }
  });
  return updated;
}

/** `tasks.batchReturnToAgent` — re-queue tasks back to their agent (pending). */
export function returnHomeTasksToAgent(taskIds: string[]): TaskResponse[] {
  const updated: TaskResponse[] = [];
  homeTasksStore.update((s) => {
    for (const t of s.tasks) {
      if (!taskIds.includes(t.id)) continue;
      t.assigneeType = 'agent';
      t.assigneeAgentDefinitionId =
        t.assigneeAgentDefinitionId ?? HOME_DEMO_SOURCING_AGENT_ID;
      t.assigneeUserId = null;
      t.status = 'pending';
      t.updatedAt = new Date();
      updated.push(t);
    }
  });
  return updated;
}
